import { useEffect, useState } from "react";
import * as StyledSocialShare from "./Styled.SocialShare";
import useStickyContent from "@/hooks/useStickyContent";

const SocialShare = ({ title, slug, description, image }) => {
  const [currentUrl, setCurrentUrl] = useState("");

  // Configure sticky behavior for SocialShare with 1200px minimum width
  const { stickyRef, shouldHide, topOffset } = useStickyContent({
    minWidth: 1200,
    topOffset: 88,
    targetElementId: "article-content",
    hideAtEnd: true,
  });

  // Construct the article URL
  useEffect(() => {
    if (slug) {
      const articleUrl = `${window.location.origin}/articles/${slug}`;
      setCurrentUrl(articleUrl);
      console.log("🔗 Constructed share URL:", articleUrl);
      console.log("🔗 Window location origin:", window.location.origin);
      console.log("🔗 Slug:", slug);
    }
  }, [slug]);

  const shareUrl = currentUrl || window.location.href;
  const shareTitle = title || "Check out this article";
  const shareDescription =
    description || "An interesting article worth reading";

  // For development/localhost, use a placeholder URL that social media can access
  // In production, this should be your actual domain
  const getShareableUrl = () => {
    // If we're on localhost/development, use a placeholder
    if (shareUrl.includes("localhost") || shareUrl.includes("127.0.0.1")) {
      console.warn(
        "⚠️ Using localhost URL - social media sharing may not work properly"
      );
      // You can replace this with your production domain for testing
      // return `https://yourdomain.com/articles/${slug}`;
    }
    return shareUrl;
  };

  const finalShareUrl = getShareableUrl();

  // Fixed social media share URLs
  const socialLinks = {
    // Facebook - Updated URL format
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      finalShareUrl
    )}&quote=${encodeURIComponent(shareTitle)}`,

    // Twitter/X - working correctly
    twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(
      finalShareUrl
    )}&text=${encodeURIComponent(shareTitle)}`,

    // LinkedIn - Fixed URL format and parameters
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(
      finalShareUrl
    )}`,

    // Pinterest - working correctly
    pinterest: `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
      finalShareUrl
    )}&description=${encodeURIComponent(shareTitle)}${
      image ? `&media=${encodeURIComponent(image)}` : ""
    }`,

    // WhatsApp - working correctly
    whatsapp: `https://wa.me/?text=${encodeURIComponent(
      `${shareTitle} - ${finalShareUrl}`
    )}`,

    // Email - fixed format with proper encoding
    email: `mailto:?subject=${encodeURIComponent(
      shareTitle
    )}&body=${encodeURIComponent(
      `I thought you might find this interesting:\n\n${shareTitle}\n\n${shareDescription}\n\nRead more: ${finalShareUrl}`
    )}`,

    // Copy to clipboard
    copy: finalShareUrl,
  };

  // Debug function to test URLs
  const debugShareUrl = (platform) => {
    console.log(`🐛 Debug ${platform}:`);
    console.log(`   Title: ${shareTitle}`);
    console.log(`   URL: ${finalShareUrl}`);
    console.log(`   Share Link: ${socialLinks[platform]}`);
    console.log(`   Encoded URL: ${encodeURIComponent(finalShareUrl)}`);
  };

  // Handle social media clicks with debugging
  const handleSocialClick = (platform, e) => {
    e.preventDefault();

    debugShareUrl(platform);

    const shareLink = socialLinks[platform];

    // Test the URL construction
    console.log(`📱 Opening ${platform} share:`, shareLink);

    // Special handling for different platforms
    if (platform === "email") {
      // For email, use window.location.href instead of popup
      window.location.href = shareLink;
      return;
    }

    // For social media platforms, use popup with better parameters
    const popupWidth = platform === "linkedin" ? 700 : 600;
    const popupHeight = platform === "linkedin" ? 500 : 400;

    const popup = window.open(
      shareLink,
      `${platform}-share`,
      `width=${popupWidth},height=${popupHeight},scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,directories=no,status=no`
    );

    // Check if popup was blocked
    if (!popup || popup.closed || typeof popup.closed === "undefined") {
      console.warn(
        `⚠️ Popup blocked for ${platform}, trying direct navigation`
      );
      window.open(shareLink, "_blank");
    }
  };

  // Handle copy to clipboard with user feedback
  const handleCopyLink = async (e) => {
    e.preventDefault();
    try {
      await navigator.clipboard.writeText(finalShareUrl);
      const link = e.target;
      const originalText = link.textContent;
      link.textContent = "Copied!";
      setTimeout(() => {
        link.textContent = originalText;
      }, 2000);
      console.log("✅ Link copied to clipboard:", finalShareUrl);
    } catch (err) {
      console.error("❌ Failed to copy link:", err);
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = finalShareUrl;
      textArea.style.position = "fixed";
      textArea.style.left = "-999999px";
      textArea.style.top = "-999999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();
      try {
        document.execCommand("copy");
        const link = e.target;
        const originalText = link.textContent;
        link.textContent = "Copied!";
        setTimeout(() => {
          link.textContent = originalText;
        }, 2000);
        console.log("✅ Link copied to clipboard (fallback)");
      } catch (fallbackErr) {
        console.error("❌ Fallback copy failed:", fallbackErr);
      }
      document.body.removeChild(textArea);
    }
  };

  // Debug all URLs on component mount
  useEffect(() => {
    if (finalShareUrl) {
      console.log("📤 SocialShare Debug Info:");
      console.log("   Title:", shareTitle);
      console.log("   Description:", shareDescription);
      console.log("   Image:", image);
      console.log("   Final Share URL:", finalShareUrl);
      console.log("   All social links:", socialLinks);
    }
  }, [finalShareUrl, shareTitle, shareDescription, image]);

  return (
    <StyledSocialShare.SocialShareWrapper
      ref={stickyRef}
      $shouldHide={shouldHide}
      $topOffset={topOffset}
    >
      <StyledSocialShare.SocialShareSummary as="summary" data-animate="fade-up">
        Like it? Why not share it?
      </StyledSocialShare.SocialShareSummary>

      <ul>
        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.twitter}
            onClick={(e) => handleSocialClick("twitter", e)}
            aria-label="Share on X (Twitter)"
          >
            X (Twitter)
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.facebook}
            onClick={(e) => handleSocialClick("facebook", e)}
            aria-label="Share on Facebook"
          >
            Facebook
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.linkedin}
            onClick={(e) => handleSocialClick("linkedin", e)}
            aria-label="Share on LinkedIn"
          >
            LinkedIn
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.pinterest}
            onClick={(e) => handleSocialClick("pinterest", e)}
            aria-label="Share on Pinterest"
          >
            Pinterest
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.whatsapp}
            onClick={(e) => handleSocialClick("whatsapp", e)}
            aria-label="Share on WhatsApp"
          >
            WhatsApp
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href={socialLinks.email}
            onClick={(e) => handleSocialClick("email", e)}
            aria-label="Share via Email"
          >
            Email
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>

        <StyledSocialShare.SocialShareList data-animate="fade-up">
          <StyledSocialShare.SocialShareLink
            href="#"
            onClick={handleCopyLink}
            aria-label="Copy link"
          >
            Copy Link
          </StyledSocialShare.SocialShareLink>
        </StyledSocialShare.SocialShareList>
      </ul>
    </StyledSocialShare.SocialShareWrapper>
  );
};

export default SocialShare;
